/**
 * Development-only color-coded logger with emojis for improved debugging experience.
 * This logger only outputs in development mode and remains silent in production.
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'success';

interface LogStyle {
  emoji: string;
  style: string;
}

/**
 * Configuration for different log levels with their corresponding emojis and CSS styles
 */
const LOG_STYLES: Record<LogLevel, LogStyle> = {
  log: {
    emoji: '📝',
    style:
      'color: #6b7280; background: #f9fafb; padding: 2px 6px; border-radius: 3px;',
  },
  info: {
    emoji: '💡',
    style:
      'color: #3b82f6; background: #eff6ff; padding: 2px 6px; border-radius: 3px;',
  },
  success: {
    emoji: '✅',
    style:
      'color: #10b981; background: #ecfdf5; padding: 2px 6px; border-radius: 3px;',
  },
  warn: {
    emoji: '⚠️',
    style:
      'color: #f59e0b; background: #fffbeb; padding: 2px 6px; border-radius: 3px;',
  },
  error: {
    emoji: '❌',
    style:
      'color: #ef4444; background: #fef2f2; padding: 2px 6px; border-radius: 3px;',
  },
};

/**
 * Checks if the application is running in development mode
 * This function can be overridden for testing purposes
 */
let isDevelopment = (): boolean => {
  return import.meta.env.VITE_APP_ENV === 'development' || import.meta.env.MODE === 'development';
};

/**
 * Override the development mode check (primarily for testing)
 * @internal - This function is only exported for testing purposes
 * @param devMode - true for development mode, false otherwise
 */
export const setDevelopmentMode = (devMode: boolean): void => {
  isDevelopment = () => devMode;
};

/**
 * Generic logging function that handles styling and environment checks
 */
const logWithStyle = (
  level: LogLevel,
  message: string,
  ...args: any[]
): void => {
  // Return silently in production mode
  if (!isDevelopment()) {
    return;
  }

  try {
    const { emoji, style } = LOG_STYLES[level];
    const styledMessage = `${emoji} %c${message}`;

    // Use the appropriate console method based on level
    const consoleMethod = level === 'success' ? 'log' : level;

    console[consoleMethod](styledMessage, style, ...args);
  } catch (error) {
    // Fallback to plain console logging if styling fails
    console.log(`${LOG_STYLES[level].emoji} ${message}`, ...args);
  }
};

/**
 * Development logger utility with color-coded output and emojis.
 * All methods are silent in production mode.
 */
export const logger = {
  /**
   * General purpose logging with gray styling
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  log: (message: string, ...args: any[]): void => {
    logWithStyle('log', message, ...args);
  },

  /**
   * Information logging with blue styling and lightbulb emoji
   * @param message - The information message to log
   * @param args - Additional arguments to log
   */
  info: (message: string, ...args: any[]): void => {
    logWithStyle('info', message, ...args);
  },

  /**
   * Success logging with green styling and checkmark emoji
   * @param message - The success message to log
   * @param args - Additional arguments to log
   */
  success: (message: string, ...args: any[]): void => {
    logWithStyle('success', message, ...args);
  },

  /**
   * Warning logging with orange styling and warning emoji
   * @param message - The warning message to log
   * @param args - Additional arguments to log
   */
  warn: (message: string, ...args: any[]): void => {
    logWithStyle('warn', message, ...args);
  },

  /**
   * Error logging with red styling and error emoji
   * @param message - The error message to log
   * @param args - Additional arguments to log
   */
  error: (message: string, ...args: any[]): void => {
    logWithStyle('error', message, ...args);
  },
};

// Export as singleton
export default logger;
